import { TKeyMetricApi } from 'types/GlobalKeyMetric'

// Helper function to recursively find and update a parent node
export const addChildToParent = (
  node: any,
  parentId: string,
  child: any
): any => {
  if (node.id === parentId) {
    return {
      ...node,
      children: [...(node.children || []), child]
    }
  }

  if (node.children && node.children.length > 0) {
    const updatedChildren = node.children.map((childNode: any) =>
      addChildToParent(childNode, parentId, child)
    )

    if (
      updatedChildren.some(
        (child: any, index: number) => child !== node.children[index]
      )
    ) {
      return {
        ...node,
        children: updatedChildren
      }
    }
  }

  return node
}

// Helper function to recursively find and remove a child node
export const removeChildFromParent = (
  node: any,
  parentId: string,
  childId: string
): any => {
  if (node.id === parentId) {
    return {
      ...node,
      children: (node.children || []).filter(
        (child: any) => child.id !== childId
      )
    }
  }

  if (node.children && node.children.length > 0) {
    const updatedChildren = node.children.map((childNode: any) =>
      removeChildFromParent(childNode, parentId, childId)
    )

    if (
      updatedChildren.some(
        (child: any, index: number) => child !== node.children[index]
      )
    ) {
      return {
        ...node,
        children: updatedChildren
      }
    }
  }

  return node
}

// Helper function to recursively find and update a child node in parent
export const updateChildInParent = (
  node: any,
  parentId: string,
  childId: string,
  updates: any
): any => {
  if (node.id === parentId) {
    return {
      ...node,
      children: (node.children || []).map((child: any) =>
        child.id === childId ? { ...child, ...updates } : child
      )
    }
  }

  if (node.children && node.children.length > 0) {
    const updatedChildren = node.children.map((childNode: any) =>
      updateChildInParent(childNode, parentId, childId, updates)
    )

    if (
      updatedChildren.some(
        (child: any, index: number) => child !== node.children[index]
      )
    ) {
      return {
        ...node,
        children: updatedChildren
      }
    }
  }

  return node
}

// Helper function to recursively remove a node by id from any depth
export const removeFromTree = (node: TKeyMetricApi, id: string): any => {
  const filteredChildren = (node.children || []).filter((c) => c.id !== id)
  const recursedChildren = filteredChildren.map((c) => removeFromTree(c, id))

  if (
    recursedChildren.length !== (node.children || []).length ||
    recursedChildren.some((c, i) => c !== filteredChildren[i])
  ) {
    return { ...node, children: recursedChildren }
  }

  return node
}

// Search a tree of nodes for a node with a given id
export const findNodeById = (
  nodes: TKeyMetricApi[],
  targetId: string
): TKeyMetricApi | null => {
  for (const node of nodes) {
    if (node.id === targetId) {
      return node
    }

    const foundInChildren = findNodeById(node.children, targetId)

    if (foundInChildren) {
      return foundInChildren
    }
  }

  return null
}
